import { useField } from 'formik';

import { Accordion } from '../../cdl/Accordion/Accordion';
import { Label } from '../../cdl/Label/Label';
import { Description, Radio, RadioGroup, RadioGroupItem } from '../../cdl/RadioGroup/RadioGroup';
import { Tag } from '../../cdl/Tag/Tag';
import { translate } from '../../common/helpers/translate.helper';
import { PreformattedText } from '../../common/PreformattedText/PreformattedText';
import { TagListInput } from '../../common/TagListInput/TagListInput';
import { Box } from '../../common/ui/Box';

interface ArrayFeatureFlagStateTagProps {
    isManualOverride: boolean;
}

const ArrayFeatureFlagStateTag = ({ isManualOverride }: ArrayFeatureFlagStateTagProps) => {
    if (isManualOverride) {
        return (
            <Tag variant="accent" emphasis>
                {translate('devTools.featureFlags.override')}
            </Tag>
        );
    }

    return <Tag variant="default">{translate('devTools.featureFlags.launchDarkly')}</Tag>;
};

interface ArrayFeatureFlagAccordionItemProps {
    name: string;
    launchDarklyValue: string[];
    storedValue: string[] | null;
}

const LAUNCHDARKLY_VALUE = 'launchdarkly';
const OVERRIDE_VALUE = 'override';

export const ArrayFeatureFlagAccordionItem = ({
    name,
    launchDarklyValue,
    storedValue,
}: ArrayFeatureFlagAccordionItemProps) => {
    const [field, , helpers] = useField<string[] | null>(name);

    const isStoredOverride = storedValue !== null;

    const getRadioValue = (): string => {
        if (field.value === null) {
            return LAUNCHDARKLY_VALUE;
        }
        return OVERRIDE_VALUE;
    };

    const handleValueChange = (value: string) => {
        if (value === LAUNCHDARKLY_VALUE) {
            helpers.setValue(null);
        } else {
            helpers.setValue([]);
        }
    };

    const handleTagListChange = (values: string[]) => {
        helpers.setValue(values);
    };

    return (
        <Accordion.Item
            id={name}
            title={name}
            summary={
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={4} width="100%">
                    <ArrayFeatureFlagStateTag isManualOverride={isStoredOverride} />
                </Box>
            }
        >
            <Box paddingLeft={8}>
                <RadioGroup value={getRadioValue()} onValueChange={handleValueChange}>
                    <RadioGroupItem>
                        <Radio value={LAUNCHDARKLY_VALUE} />
                        <Label>{translate('devTools.featureFlags.launchDarkly')}</Label>
                        <Description as="div">
                            <Box marginBottom={8}>
                                <PreformattedText>{JSON.stringify(launchDarklyValue, null, 2)}</PreformattedText>
                            </Box>
                        </Description>
                    </RadioGroupItem>
                    <RadioGroupItem>
                        <Radio value={OVERRIDE_VALUE} />
                        <Label>{translate('devTools.featureFlags.override')}</Label>
                        <Description as="div">
                            <Box marginTop={4}>
                                <TagListInput values={field.value ?? []} onChange={handleTagListChange} />
                            </Box>
                        </Description>
                    </RadioGroupItem>
                </RadioGroup>
            </Box>
        </Accordion.Item>
    );
};
